import React, { useCallback, useState } from 'react'
import {
  QuickAuditForm,
  QuickIncidentForm,
  QuickMissionForm,
  QuickPipelineForm,
  QuickPlanForm,
  QuickProjectForm,
  QuickRiskForm,
} from './QuickCreateForms'
import { QuickEntity } from './quickCreateErrors'

export type QuickCreateRequest = {
  kind: QuickEntity
  /** Text typed in the select, used as the default name. */
  name?: string
  /** Parent mission, prefilled and locked in the form. */
  missionId?: string
}

export type QuickCreated = { _id?: string } & Record<string, any>

type QuickCreateProps = {
  request: QuickCreateRequest | null
  onClose: () => void
  onCreated: (kind: QuickEntity, created: QuickCreated) => void
}

export default function QuickCreate({ request, onClose, onCreated }: QuickCreateProps) {
  if (!request) return null
  const props = {
    initialName: request.name,
    missionId: request.missionId,
    onCancel: onClose,
    onCreated: (created: QuickCreated) => onCreated(request.kind, created),
  }
  switch (request.kind) {
    case 'mission':
      return <QuickMissionForm {...props} />
    case 'pipeline':
      return <QuickPipelineForm {...props} />
    case 'project':
      return <QuickProjectForm {...props} />
    case 'audit':
      return <QuickAuditForm {...props} />
    case 'incident':
      return <QuickIncidentForm {...props} />
    case 'risk':
      return <QuickRiskForm {...props} />
    case 'plan':
      return <QuickPlanForm {...props} />
    default:
      return null
  }
}

/** Open/close state plus one reload key per entity, for the selects to refetch. */
export function useQuickCreate() {
  const [request, setRequest] = useState<QuickCreateRequest | null>(null)
  const [keys, setKeys] = useState<Partial<Record<QuickEntity, number>>>({})

  const open = useCallback(
    (kind: QuickEntity, missionId?: string) => (name: string) => setRequest({ kind, name, missionId }),
    []
  )
  const close = useCallback(() => setRequest(null), [])
  const bump = useCallback(
    (kind: QuickEntity) => setKeys((k) => ({ ...k, [kind]: (k[kind] ?? 0) + 1 })),
    []
  )
  const keyOf = (kind: QuickEntity) => keys[kind] ?? 0

  return { request, open, close, bump, keyOf }
}
